import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { StarIcon, XIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon, HeadphonesIcon } from 'lucide-react';
import { genreColors, formatIcons } from './constants';
import { chunkBooksIntoShelves } from './utils';
import { BookHoverCard } from './BookHoverCard';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
// Popup Modal
export const NarratorBookInfoModal: React.FC<{
  book: any;
  darkMode: boolean;
  onClose: () => void;
  onEdit: (bookId: string) => void;
}> = ({
  book,
  darkMode,
  onClose,
  onEdit
}) => {
  const rt = useRoleTheme();
  if (!book) return null;
  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  return <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center" onClick={onClose} data-id="element-20256" data-mp="1yfwhwbf6ndmx">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-20257" data-mp="3aui6dgfsqxuf" />
      <div className={`relative w-full md:max-w-md md:rounded-2xl rounded-t-2xl shadow-2xl border-t md:border ${bg} ${border}`} onClick={e => e.stopPropagation()} data-id="element-20258" data-mp="3esfs29acz1t2">
        <div className="flex justify-center pt-3 pb-1 md:hidden" data-id="element-20259" data-mp="3oqvliiwuyn5q">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-20260" data-mp="2r4y8h118za16" />
        </div>
        <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} transition-colors`} data-id="element-20261" data-mp="2w4atrhch091n">
          <XIcon className="h-4 w-4" data-id="element-20262" data-mp="35v5egha2j0kz" />
        </button>

        <div className="px-5 pb-6 pt-3 md:pt-5" data-id="element-20263" data-mp="28d5fv7q3bqtz">
          <div className="flex gap-4 items-start" data-id="element-20264" data-mp="2dd1ip3n1xc7y">
            <div className="flex-shrink-0 w-24 h-36 rounded-xl overflow-hidden shadow-lg" data-id="element-20265" data-mp="2dbgoxg8k9xb5">
              <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" data-id="element-20266" data-mp="25b0xh20o9qon" />
            </div>

            <div className="flex-1 min-w-0" data-id="element-20267" data-mp="2f2er4t5xzw9v">
              <h3 className={`font-bold text-base leading-tight line-clamp-2 mb-0.5 ${textPrimary}`} data-id="element-20268" data-mp="2f49e0d0rxygl">
                {book.title}
              </h3>
              {book.series && <p className="text-xs text-purple-500 mb-1 font-medium" data-id="element-20269" data-mp="1mfarz4tj6c60">
                  {book.series} #{book.seriesNumber}
                </p>}
              <p className={`text-xs mb-1.5 ${textSecondary}`} data-id="element-20270" data-mp="1wcgnqqd3arck">
                Written by {book.writtenAuthor}
              </p>

              <div className="flex flex-wrap gap-1 mb-1.5" data-id="element-20271" data-mp="1rex7jkwu04hz">
                {book.genre && <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-20272" data-mp="37sx3kksk39zy">
                    {book.genre}
                  </span>}
                <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium ${darkMode ? 'bg-blue-900/60 text-blue-200' : 'bg-blue-100 text-blue-700'}`} data-id="element-20273" data-mp="tkmhkhqf3cxm">
                  {book.runtime}
                </span>
              </div>

              <div className="flex items-center gap-2" data-id="element-20274" data-mp="187peam6x1hjw">
                <div className="flex items-center gap-1" data-id="element-20275" data-mp="18ablultt3x24">
                  <StarIcon className="h-3.5 w-3.5 text-amber-500 fill-current" data-id="element-20276" data-mp="1c89c7rzbcp73" />
                  <span className={`text-sm font-semibold ${textPrimary}`} data-id="element-20277" data-mp="yixnwihi75b3">
                    {book.rating}
                  </span>
                </div>
                <span className={`text-xs ${textSecondary}`} data-id="element-20278" data-mp="13ewb42r3ih9x">
                  {book.releaseDate.substring(0, 4)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-3" data-id="element-20279" data-mp="1vzmqieea2blj">
            <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-20280" data-mp="7c8l81wvy20g">
              <HeadphonesIcon className="h-3 w-3" data-id="element-20281" data-mp="3ufwvm0gynnw6" />
              <span data-id="element-20282" data-mp="3ugor7bak5lmg">Audiobook</span>
            </span>
          </div>

          <div className="mt-4 flex gap-2" data-id="element-20283" data-mp="3pidhr1o6dd4r">
            <button onClick={() => {
            onEdit(book.id);
            onClose();
          }} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-id="element-20284" data-mp="3pjolx2lj8mwe">
              <PencilIcon className="h-4 w-4" data-id="element-20285" data-mp="catq4en319v8" />
              Edit Narration
            </button>
            <button onClick={onClose} className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${darkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-20286" data-mp="19q7j3u3qce6n">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
};
// Cover Carousel (Native Scroll Pattern)
export const CoverCarousel: React.FC<{
  books: any[];
  darkMode: boolean;
  onBookClick: (book: any) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
}> = ({
  books,
  darkMode,
  onBookClick,
  onHoverEnter,
  onHoverLeave
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    if (scrollRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [books]);
  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = scrollRef.current.clientWidth * 0.75;
      scrollRef.current.scrollBy({
        left: dir === 'left' ? -amount : amount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 350);
    }
  };
  return <div className="relative group" data-id="element-20287" data-mp="xm8tvg92qoi9">
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} onScroll={checkScroll} data-id="element-20288" data-mp="xl7hn8gbnvyy">
        {books.map((book: any) => <div key={book.id} className="flex-shrink-0 w-28 sm:w-32 cursor-pointer group/book" onClick={() => onBookClick(book)} onMouseEnter={() => onHoverEnter(book.id)} onMouseLeave={onHoverLeave} data-id="element-20289" data-mp="3ddwkyv1epmt5">
            <div className="w-full aspect-[2/3] bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover/book:scale-[1.04] group-hover/book:shadow-xl" style={{
          backgroundImage: `url('${book.coverImage}')`
        }} title={book.title} data-id="element-20290" data-mp="1sujawuil9ubj" />
          </div>)}
      </div>

      {canScrollLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 p-2 rounded-full shadow-md transition-colors ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-20291" data-mp="1a5cr14626wo5">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-20292" data-mp="2qj2xuiokbz1h" />
        </button>}
      {canScrollRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 p-2 rounded-full shadow-md transition-colors ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-20293" data-mp="2ujmu7k1baymk">
          <ChevronRightIcon className="h-4 w-4" data-id="element-20294" data-mp="18ix5i9dfe4ec" />
        </button>}
    </div>;
};
// Main Item Display — titled shelf section (spine row or cover grid) for a
// group of narrator audiobooks. Opens NarratorBookInfoModal on tap.
interface ItemDisplayProps {
  books: any[];
  viewMode: 'cover' | 'spine';
  darkMode: boolean;
  booksPerShelf?: number;
  hoveredBookId?: string | null;
  setHoveredBookId?: (id: string | null) => void;
  mousePosition?: {
    x: number;
    y: number;
  };
  title?: string;
  showShelves?: boolean;
  navigateToBookEdit?: (id: string) => void;
}
export const ItemDisplay: React.FC<ItemDisplayProps> = ({
  books,
  viewMode,
  darkMode,
  booksPerShelf,
  hoveredBookId,
  setHoveredBookId,
  mousePosition,
  title,
  showShelves = true,
  navigateToBookEdit
}) => {
  const [selectedBook, setSelectedBook] = useState<any | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const safeBooks = Array.isArray(books) ? books : [];
  const handleSpineTap = (book: any) => {
    if (isMobile) {
      setSelectedBook(book);
    } else if (navigateToBookEdit) {
      navigateToBookEdit(book.id);
    } else {
      setSelectedBook(book);
    }
  };
  const SpineBook = ({
    book
  }: {
    book: any;
  }) => <div onClick={() => handleSpineTap(book)} onMouseEnter={() => !isMobile && setHoveredBookId?.(book.id)} onMouseLeave={() => setHoveredBookId?.(null)} className="relative flex flex-col items-center justify-center text-white font-medium rounded transition-transform duration-200 ease-in-out cursor-pointer text-center hover:scale-105 select-none flex-shrink-0" style={{
    width: 56,
    height: 220,
    fontSize: '0.85rem',
    backgroundColor: genreColors[!book.coverImage ? book.genre : ''] || genreColors.default,
    backgroundImage: book.coverImage ? `url(${book.coverImage})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    marginBottom: 4
  }} title={book.title} data-id="element-20295" data-mp="28lv3n1fm72c6">
      {!book.coverImage && <div className="flex flex-col items-center px-1 w-full h-full justify-center" style={{
      writingMode: 'vertical-rl'
    }} data-id="element-20296" data-mp="af3umayzgen6">
          <div className="w-full truncate" data-id="element-20297" data-mp="fgb2sb51ffud">{book.title}</div>
          {book.seriesNumber && <div className="mt-2 text-xs font-normal opacity-80" data-id="element-20298" data-mp="jhwmogfzoue2">
              #{book.seriesNumber}
            </div>}
        </div>}
      <div className="absolute bottom-1 left-1 inline-flex items-center justify-center p-1 rounded bg-black bg-opacity-70 text-white text-xs z-10" title="Audiobook" data-id="element-20299" data-mp="1uvqk1g711d4g">
        <HeadphonesIcon className="h-3 w-3" data-id="element-20300" data-mp="29yi9qkasx4qh" />
      </div>
    </div>;
  const renderShelf = (shelfBooks: any[], shelfKey: string | number) => <div className="relative" key={shelfKey} data-id="element-20301" data-mp="24zntnq47xua2">
      <div className="flex items-end gap-2 overflow-x-auto pb-6 scrollbar-thin" style={{
      minHeight: 260
    }} data-id="element-20302" data-mp="207xcb06v6695">
        {shelfBooks.map((book: any) => <SpineBook key={book.id} book={book} data-id="element-20303" data-mp="1r77nwhjxni5g" />)}
        <div className={`absolute bottom-0 left-0 right-0 h-2 ${darkMode ? 'bg-purple-900' : 'bg-purple-600'} rounded-full`} data-id="element-20304" data-mp="1r8siu8a5t33u" />
      </div>
      {!isMobile && hoveredBookId && (() => {
      const hoveredBook = shelfBooks.find((book: any) => book.id === hoveredBookId);
      return hoveredBook && mousePosition ? <BookHoverCard book={hoveredBook} darkMode={darkMode} mousePosition={mousePosition} data-id="element-20305" data-mp="3bmssiql5sfrt" /> : null;
    })()}
    </div>;
  const displayBooks = typeof booksPerShelf === 'number' && !showShelves ? safeBooks.slice(0, booksPerShelf) : safeBooks;
  const wrapInCard = (children: React.ReactNode) => title ? <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-8`} data-id="element-20306" data-mp="vzyndna7dr1a">
        <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-20307" data-mp="1008qms6m23cg">
          {title}
        </h2>
        {children}
      </div> : <>{children}</>;
  return <>
      {wrapInCard(displayBooks.length === 0 ? <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-20308" data-mp="1eolqi08tk6c8">
            No audiobooks to display.
          </p> : viewMode === 'spine' ? showShelves && typeof booksPerShelf === 'number' ? chunkBooksIntoShelves(displayBooks, booksPerShelf).map((shelf: any[], i: number) => renderShelf(shelf, i)) : renderShelf(displayBooks, 'single') : <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6" data-id="element-20309" data-mp="3rv1yaqoho5j1">
            {displayBooks.map((book: any) => <div key={book.id} className="relative flex flex-col items-center cursor-pointer group" onClick={() => setSelectedBook(book)} onMouseEnter={() => !isMobile && setHoveredBookId?.(book.id)} onMouseLeave={() => setHoveredBookId?.(null)} data-id="element-20310" data-mp="4qdoeewo8qxz">
                <div className="relative w-full" data-id="element-20311" data-mp="3mxtb2bohhbax">
                  <div className="w-full aspect-[2/3] bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover:scale-[1.03] group-hover:shadow-xl" style={{
            backgroundImage: `url('${book.coverImage}')`
          }} title={book.title} data-id="element-20312" data-mp="3i59yni1j8m7p" />
                </div>
              </div>)}
          </div>)}
      {selectedBook && <NarratorBookInfoModal book={selectedBook} darkMode={darkMode} onClose={() => setSelectedBook(null)} onEdit={navigateToBookEdit ?? (() => {})} data-id="element-20313" data-mp="21pw1iy1i2ql1" />}
    </>;
};